import { Component, signal, effect, inject, untracked, computed } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { PageHeader } from '../../../../shared/components/page-header/page-header';
import { ProfilesService } from '../../../../shared/services/profiles.service';
import { MemberCard } from '../../../../shared/components/member-card/member-card';
import { AdvisorsSidebar } from '../../../../shared/components/advisors-sidebar/advisors-sidebar';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-advisors-page',
  imports: [PageHeader, MemberCard, AdvisorsSidebar],
  templateUrl: './advisors-page.html',
  styleUrl: './advisors-page.scss',
})
export class AdvisorsPage {
  private profilesService = new ProfilesService();
  activatedRoute = inject(ActivatedRoute);
  
  breadcrumbs = [
    { label: 'Home', route: '/' },
    { label: 'Asesores', isActive: true }
  ];

  advisors = signal<any[]>([]);
  currentPage = signal(1);
  totalPages = signal(1);
  pageSize = 9;
  isLoading = signal(false);
  advisorType = signal('');
  queryTags = signal<string[]>([]);

  icon = computed(() => {
    return (advisor: any) => {
      if (advisor.advisor_type?.value === 'collaborator') {
        return 'images/categories/colabs.svg';
      }

      if (advisor.advisor_type?.value === 'expert') {
        return 'images/categories/experts.svg';
      }

      if (advisor.advisor_type?.value === 'ies') {
        return 'images/categories/especialistas.svg';
      }

      return null;
    }
  });

  constructor(private titleService: Title, private metaService: Meta) {
    this.titleService.setTitle('Agenda una sesión de asesoramiento empresarial especializado');
    this.metaService.updateTag({ name: 'description', content: 'Agenda asesoramiento empresarial en Bolivia con experiencia real. Elige expertos, asesores o colaboradores según tu necesidad y toma mejores decisiones.' });
    this.metaService.updateTag({ name: 'keywords', content: 'Agenda asesoramiento empresarial en Bolivia, expertos empresariales Bolivia, asesoramiento empresarial Bolivia, estrategia empresarial Bolivia, decisiones empresariales' });
    this.metaService.updateTag({ property: 'og:title', content: 'Agenda una sesión de asesoramiento empresarial especializado' });
    this.metaService.updateTag({
      property: 'og:description',
      content: 'Agenda asesoramiento empresarial en Bolivia con experiencia real. Elige expertos, asesores o colaboradores según tu necesidad y toma mejores decisiones.',
    });
    this.metaService.updateTag({
      property: 'og:image',
      content: 'https://web.dev.consultorio.biz/images/SEO/Agenda_con.png',
    });

    this.activatedRoute.params.subscribe((params) => {
      this.advisorType.set(params['type']);
      this.queryTags.set([]); // Clear tags when advisor type changes
    });

    const queryParams = this.activatedRoute.snapshot.queryParams;
    const tags = Object.keys(queryParams).map(key =>  queryParams[key]);
    this.queryTags.set(tags);


    // Watch for page and advisor type changes and reload advisors
    effect(() => {
      this.currentPage();
      this.advisorType(); // track advisor type changes (route param)


      this.breadcrumbs = [
        { label: 'Home', route: '/' },
        { label: this.getAdvisorTypeLabel(this.advisorType()), isActive: true },
      ];

      
      untracked(() => this.loadAdvisors());
    });
  }

  getAdvisorTypeLabel(type: string): string {
    switch (type) {
      case 'collaborator':
        return 'Colabs';
      case 'expert':
        return 'Experts';
      case 'ies':
        return 'Asesores';
      default:
        return 'Agenda con';
    }
  }

  async loadAdvisors(query: string = '', tag: string | null = null) {
    this.isLoading.set(true);
  
    const result = await this.profilesService.getPaginatedProfilesFromApi('advisor', this.advisorType(), this.currentPage(), this.pageSize, query, tag ? [tag] : this.queryTags());
    this.advisors.set(result.data);
    this.totalPages.set(result.totalPages);
    
    this.isLoading.set(false);
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
      this.scrollToTop();
    }
  }

  scrollToTop() {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  async handleSearch(query: string) {
    await this.loadAdvisors(query);
  }

  async handleSelectedTag(tag: string) {
    this.queryTags.set([]);
    await this.loadAdvisors('', tag);
  }
}
